import { Row } from '@tanstack/react-table';

import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { Switch } from '@/components/ui/switch';
import i18n from '@/core/i18n';
import { cn } from '@/lib/utils';

import { GenerateBillListItem } from '../../../types';
import { hasMemberQuickBooksError } from '../bill-row.helpers';
import { MonthlySalaryActions } from '../generate-bills-table.types';

/**
 * Per-member billing-mode switch. Rendered only on member rows: sub-rows inherit the member's
 * mode, and ineligible members get a disabled switch with an explanatory tooltip rather than a
 * hidden control, so the absence of the option is never silent.
 */
export const MonthlySalarySwitch = ({
  row,
  monthlySalaryActions,
}: {
  row: Row<GenerateBillListItem>;
  monthlySalaryActions: MonthlySalaryActions;
}) => {
  const { canSelectMonthlySalary, isMonthlySalaryEnabled, id } = row.original;
  const isDisabled = !canSelectMonthlySalary || Boolean(hasMemberQuickBooksError(row));

  const control = (
    <div className="flex items-center gap-2">
      <Switch
        checked={isMonthlySalaryEnabled}
        disabled={isDisabled}
        onCheckedChange={checked => monthlySalaryActions.toggleMonthlySalary(id, checked)}
        onClick={event => event.stopPropagation()}
        aria-label={i18n.t('authenticated.bills:monthlySalary.toggleAriaLabel')}
      />
      <span className={cn('text-sm', isDisabled && 'text-muted-foreground')}>
        {i18n.t('authenticated.bills:monthlySalary.label')}
      </span>
    </div>
  );

  if (!isDisabled) return control;

  // Never leave the switch disabled without saying why. Ineligibility (mixed billing model in
  // the period) and a missing QuickBooks link are different problems with different fixes, so
  // they get different copy.
  const disabledTooltipKey = canSelectMonthlySalary
    ? 'authenticated.bills:monthlySalary.quickBooksTooltip'
    : 'authenticated.bills:monthlySalary.ineligibleTooltip';

  return (
    <SimpleTooltip content={i18n.t(disabledTooltipKey)} contentClassName="max-w-xs">
      <span className="inline-flex cursor-help">{control}</span>
    </SimpleTooltip>
  );
};
